import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Heading,
  VStack,
  HStack,
  useToast,
  IconButton,
  Text,
  Divider,
  Checkbox,
  NumberInput,
  NumberInputField,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { CREATE_MATCH_FORMAT, LIST_MATCH_FORMATS } from '../../Queries';
import { useUser } from '../../Context';
import { Footer, WithSubnavigation } from '../../Components';

export const CreateFormat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useUser();

  // Check if we're copying from an existing format
  const copyFrom = location.state?.copyFrom;

  const [formData, setFormData] = useState({
    format_name: copyFrom?.format_name || '',
    match_category: copyFrom?.match_category || 'Rifle',
    match_discipline: copyFrom?.match_discipline || '',
    competition_type: copyFrom?.competition_type || 'RANKED',
    stage_aggregation_method: copyFrom?.stage_aggregation_method || 'SUM',
    is_template: copyFrom?.is_template !== undefined ? copyFrom.is_template : true,
    divisions: copyFrom?.divisions?.length > 0 ? copyFrom.divisions : ['Open'],
    categories: copyFrom?.categories?.length > 0 ? copyFrom.categories : ['Adult'],
  });

  const [bracketConfig, setBracketConfig] = useState({
    bracket_type: copyFrom?.bracket_config?.bracket_type || 'SINGLE_ELIMINATION',
    seeding_method: copyFrom?.bracket_config?.seeding_method || 'MANUAL',
    num_participants: copyFrom?.bracket_config?.num_participants || null,
  });

  const [tiebreakerEnabled, setTiebreakerEnabled] = useState(
    copyFrom?.tiebreaker_rules?.enabled || false
  );
  
  const [tiebreakerSteps, setTiebreakerSteps] = useState(() => {
    if (copyFrom?.tiebreaker_rules?.steps?.length > 0) {
      return copyFrom.tiebreaker_rules.steps;
    }
    return [];
  });

  const [stages, setStages] = useState(() => {
    if (copyFrom?.stages?.length > 0) {
      // Parse stages from copied format
      return copyFrom.stages.map(stage => ({
        stage_name: stage.stage_name,
        stage_order: stage.stage_order,
        target_count: stage.target_count || 1,
        target_aggregation_method: stage.target_aggregation_method || 'SUM',
        scoring_primitive: stage.scoring_primitive,
        weight: stage.weight || 1.0,
        stage_points: stage.stage_points || 100,
        parameters: typeof stage.parameters === 'string' 
          ? JSON.parse(stage.parameters)
          : stage.parameters || { max_score: 100, shots_per_target: 10, x_count: false }
      }));
    }
    return [
      {
        stage_name: 'Stage 1',
        stage_order: 1,
        target_count: 1,
        target_aggregation_method: 'SUM',
        scoring_primitive: 'SCORING_RING',
        weight: 1.0,
        stage_points: 100,
        parameters: {
          max_score: 100,
          shots_per_target: 10,
          x_count: false,
        },
      },
    ];
  });

  const [createFormat, { loading }] = useMutation(CREATE_MATCH_FORMAT, {
    onCompleted: () => {
      toast({
        title: 'Match format created successfully',
        status: 'success',
        duration: 3000,
      });
      navigate('/matches/manage/formats');
    },
    onError: (error) => {
      toast({
        title: 'Error creating format',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    refetchQueries: [{ query: LIST_MATCH_FORMATS, variables: { is_template: true } }],
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDivisionChange = (index, value) => {
    const newDivisions = [...formData.divisions];
    newDivisions[index] = value;
    setFormData((prev) => ({ ...prev, divisions: newDivisions }));
  };

  const addDivision = () => {
    setFormData((prev) => ({
      ...prev,
      divisions: [...prev.divisions, ''],
    }));
  };

  const removeDivision = (index) => {
    setFormData((prev) => ({
      ...prev,
      divisions: prev.divisions.filter((_, i) => i !== index),
    }));
  };

  const handleCategoryChange = (index, value) => {
    const newCategories = [...formData.categories];
    newCategories[index] = value;
    setFormData((prev) => ({ ...prev, categories: newCategories }));
  };

  const addCategory = () => {
    setFormData((prev) => ({
      ...prev,
      categories: [...prev.categories, ''],
    }));
  };

  const removeCategory = (index) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index),
    }));
  };

  const handleStageChange = (index, field, value) => {
    const newStages = [...stages];
    if (field.startsWith('parameters.')) {
      const paramField = field.split('.')[1];
      newStages[index].parameters = {
        ...newStages[index].parameters,
        [paramField]: value,
      };
    } else {
      newStages[index][field] = value;
      
      // Reset parameters when scoring primitive changes
      if (field === 'scoring_primitive') {
        newStages[index].parameters = getDefaultParametersForPrimitive(value);
      }
    }
    setStages(newStages);
  };

  // Helper function to get default parameters for a scoring primitive
  const getDefaultParametersForPrimitive = (primitive) => {
    switch (primitive) {
      case 'SCORING_RING':
        return { max_score: 100, shots_per_target: 10, x_count: false, inner_tens: false, clean_targets: false };
      case 'HIT_MISS':
        return { shots_per_target: 1 };
      case 'TIME_BASED':
        return { time_limit: null, par_time: null };
      case 'COUNT_BASED':
        return { shots_per_target: 1 };
      case 'HIT_FACTOR':
        return { max_score: 100, shots_per_target: 10, time_limit: null };
      case 'TIME_PLUS':
        return { max_score: 100, shots_per_target: 10, time_limit: null };
      default:
        return { max_score: 100, shots_per_target: 10 };
    }
  };

  const addStage = () => {
    setStages([
      ...stages,
      {
        stage_name: `Stage ${stages.length + 1}`,
        stage_order: stages.length + 1,
        target_count: 1,
        target_aggregation_method: 'SUM',
        scoring_primitive: 'SCORING_RING',
        weight: 1.0,
        stage_points: 100,
        parameters: {
          max_score: 100,
          shots_per_target: 10,
          x_count: false,
        },
      },
    ]);
  };

  const removeStage = (index) => {
    const newStages = stages.filter((_, i) => i !== index);
    // Renumber stages
    newStages.forEach((stage, i) => {
      stage.stage_order = i + 1;
    });
    setStages(newStages);
  };

  const addTiebreakerStep = () => {
    setTiebreakerSteps([
      ...tiebreakerSteps,
      {
        order: tiebreakerSteps.length + 1,
        type: 'AGGREGATE_SCORE_FIELD',
        field: 'x_count',
        direction: 'DESC',
        label: 'Total X Count',
      },
    ]);
  };

  const removeTiebreakerStep = (index) => {
    const newSteps = tiebreakerSteps.filter((_, i) => i !== index);
    // Renumber steps
    newSteps.forEach((step, i) => {
      step.order = i + 1;
    });
    setTiebreakerSteps(newSteps);
  };

  const updateTiebreakerStep = (index, field, value) => {
    const newSteps = [...tiebreakerSteps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: value,
    };
    
    // Initialize related fields when type changes
    if (field === 'type') {
      if (value === 'SHOOTOFF') {
        // Initialize shootoff_type to FIXED_TARGETS if not set
        if (!newSteps[index].shootoff_type) {
          newSteps[index].shootoff_type = 'FIXED_TARGETS';
        }
      }
    }
    
    setTiebreakerSteps(newSteps);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert stages to the format expected by GraphQL
    const stagesInput = stages.map((stage) => {
      // Filter parameters to only include allowed fields and remove null/undefined
      const allowedParams = {};
      if (stage.parameters.max_score !== undefined && stage.parameters.max_score !== null) {
        allowedParams.max_score = parseInt(stage.parameters.max_score);
      }
      if (stage.parameters.shots_per_target !== undefined && stage.parameters.shots_per_target !== null) {
        allowedParams.shots_per_target = parseInt(stage.parameters.shots_per_target);
      }
      if (stage.parameters.min_shots_per_target !== undefined && stage.parameters.min_shots_per_target !== null) {
        allowedParams.min_shots_per_target = parseInt(stage.parameters.min_shots_per_target);
      }
      if (stage.parameters.time_limit !== undefined && stage.parameters.time_limit !== null) {
        allowedParams.time_limit = parseFloat(stage.parameters.time_limit);
      }
      if (stage.parameters.par_time !== undefined && stage.parameters.par_time !== null) {
        allowedParams.par_time = parseFloat(stage.parameters.par_time);
      }
      if (stage.parameters.x_count !== undefined && stage.parameters.x_count !== null) {
        allowedParams.x_count = Boolean(stage.parameters.x_count);
      }
      if (stage.parameters.inner_tens !== undefined && stage.parameters.inner_tens !== null) {
        allowedParams.inner_tens = Boolean(stage.parameters.inner_tens);
      }
      if (stage.parameters.clean_targets !== undefined && stage.parameters.clean_targets !== null) {
        allowedParams.clean_targets = Boolean(stage.parameters.clean_targets);
      }
      
      return {
        stage_name: stage.stage_name,
        stage_order: stage.stage_order,
        target_count: parseInt(stage.target_count) || 1,
        target_aggregation_method: stage.target_aggregation_method,
        scoring_primitive: stage.scoring_primitive,
        weight: parseFloat(stage.weight),
        stage_points: stage.stage_points ? parseInt(stage.stage_points) : null,
        parameters: allowedParams,
      };
    });

    // Build tie-breaker rules if enabled
    let tiebreakerRules = null;
    if (tiebreakerEnabled && tiebreakerSteps.length > 0) {
      tiebreakerRules = {
        enabled: true,
        method: 'SEQUENTIAL',
        steps: tiebreakerSteps.map((step) => ({
          order: step.order,
          type: step.type,
          field: step.field || null,
          stage_id: step.stage_id || null,
          shootoff_type: step.shootoff_type || null,
          target_count: step.target_count ? parseInt(step.target_count) : null,
          shots_per_round: step.shots_per_round ? parseInt(step.shots_per_round) : null,
          direction: step.direction || null,
          label: step.label,
        })),
      };
    }

    // Build bracket config if BRACKET competition type
    let bracketConfigInput = null;
    if (formData.competition_type === 'BRACKET') {
      bracketConfigInput = {
        bracket_type: bracketConfig.bracket_type,
        seeding_method: bracketConfig.seeding_method,
        num_participants: bracketConfig.num_participants ? parseInt(bracketConfig.num_participants) : null,
      };
    }

    const variables = {
      format_details: {
        format_name: formData.format_name,
        match_category: formData.match_category,
        match_discipline: formData.match_discipline || null,
        competition_type: formData.competition_type,
        bracket_config: bracketConfigInput,
        stage_aggregation_method: formData.stage_aggregation_method,
        is_template: formData.is_template,
        divisions: formData.divisions.filter((d) => d.trim() !== ''),
        categories: formData.categories.filter((c) => c.trim() !== ''),
        stages: stagesInput,
        tiebreaker_rules: tiebreakerRules,
      },
    };

    createFormat({ variables });
  };

  return (
    <Box bg="#F7F7F7" minH="100vh">
      <WithSubnavigation />
      <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
        <Box maxW="1000px" mx="auto">
          <Heading size="lg" mb={6}>
            Create Match Format
          </Heading>

          {copyFrom && (
            <Alert status="info" mb={6}>
              <AlertIcon />
              Creating a copy of an existing format. You can edit all fields before saving.
            </Alert>
          )}

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Basic Information */}
          <Box>
            <Heading size="md" mb={4}>
              Basic Information
            </Heading>

            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Format Name</FormLabel>
                <Input
                  name="format_name"
                  value={formData.format_name}
                  onChange={handleChange}
                  placeholder="e.g., NMC 200 Yard Slow Fire"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  name="match_category"
                  value={formData.match_category}
                  onChange={handleChange}
                >
                  <option value="Rifle">Rifle</option>
                  <option value="Pistol">Pistol</option>
                  <option value="Shotgun">Shotgun</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Discipline</FormLabel>
                <Select
                  name="match_discipline"
                  value={formData.match_discipline}
                  onChange={handleChange}
                  placeholder="Select discipline (optional)"
                >
                  <option value="CMP_HIGHPOWER">CMP Highpower</option>
                  <option value="COWBOY_SILHOUETTE">Cowboy Silhouette</option>
                  <option value="BLACK_POWDER_SILHOUETTE">Black Powder Silhouette</option>
                  <option value="NRA_ACTION_PISTOL">NRA Action Pistol</option>
                  <option value="BOWLING_PIN_MATCH">Bowling Pin Match</option>
                  <option value="TRAP_LEAGUE">Trap League</option>
                  <option value="SKEET_LEAGUE">Skeet League</option>
                  <option value="FIVE_STAND_LEAGUE">5-Stand League</option>
                  <option value="TOURNAMENTS">Tournaments</option>
                  <option value="OTHER">Other</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Competition Type</FormLabel>
                <Select
                  name="competition_type"
                  value={formData.competition_type}
                  onChange={handleChange}
                >
                  <option value="RANKED">Ranked (scored match with rankings)</option>
                  <option value="BRACKET">Bracket (elimination tournament)</option>
                </Select>
              </FormControl>

              {formData.competition_type === 'BRACKET' && (
                <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
                  <VStack spacing={3} align="stretch">
                    <Text fontWeight="bold" color="blue.800">Bracket Configuration</Text>
                    
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Bracket Type</FormLabel>
                      <Select
                        size="sm"
                        value={bracketConfig.bracket_type}
                        onChange={(e) => setBracketConfig({...bracketConfig, bracket_type: e.target.value})}
                      >
                        <option value="SINGLE_ELIMINATION">Single Elimination</option>
                        <option value="DOUBLE_ELIMINATION">Double Elimination</option>
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Seeding Method</FormLabel>
                      <Select
                        size="sm"
                        value={bracketConfig.seeding_method}
                        onChange={(e) => setBracketConfig({...bracketConfig, seeding_method: e.target.value})}
                      >
                        <option value="MANUAL">Manual (director assigns seeds)</option>
                        <option value="RANDOM">Random Draw</option>
                        <option value="QUALIFICATION">Based on Qualification Scores</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">Expected Participants (optional)</FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        value={bracketConfig.num_participants || ''}
                        onChange={(e) => setBracketConfig({...bracketConfig, num_participants: e.target.value})}
                        placeholder="e.g., 16"
                      />
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Bracket size will be next power of 2 (4, 8, 16, 32, 64)
                      </Text>
                    </FormControl>
                  </VStack>
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Stage Aggregation Method</FormLabel>
                <Select
                  name="stage_aggregation_method"
                  value={formData.stage_aggregation_method}
                  onChange={handleChange}
                >
                  <option value="SUM">Sum (add all stage scores)</option>
                  <option value="AVERAGE">Average (mean of stage scores)</option>
                  <option value="BEST_OF">Best Of (highest stage score)</option>
                  <option value="WEIGHTED_SUM">Weighted Sum (stages have different weights)</option>
                  <option value="TOTAL_TIME">Total Time (sum of times)</option>
                  <option value="LOWEST_TIME">Lowest Time (fastest stage)</option>
                  <option value="MATCH_POINTS">Match Points (Hit Factor - USPSA/IPSC)</option>
                </Select>
              </FormControl>

              {formData.stage_aggregation_method === 'MATCH_POINTS' && (
                <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
                  <Text fontSize="sm" color="blue.800">
                    <strong>Match Points Scoring:</strong> Each stage calculates a Hit Factor (points ÷ time). 
                    The stage winner gets 100% of stage points, others get points proportional to the winner's Hit Factor. 
                    Final ranking is by total match points across all stages.
                  </Text>
                </Box>
              )}

              <FormControl>
                <Checkbox
                  name="is_template"
                  isChecked={formData.is_template}
                  onChange={handleChange}
                >
                  Save as template (reusable for future matches)
                </Checkbox>
              </FormControl>
            </VStack>
          </Box>

          <Divider />

          {/* Divisions */}
          <Box>
            <HStack justify="space-between" mb={4}>
              <Heading size="md">Divisions</Heading>
              <Button size="sm" leftIcon={<AddIcon />} onClick={addDivision}>
                Add Division
              </Button>
            </HStack>

            <VStack spacing={2} align="stretch">
              {formData.divisions.map((division, index) => (
                <HStack key={index}>
                  <Input
                    value={division}
                    onChange={(e) => handleDivisionChange(index, e.target.value)}
                    placeholder="e.g., Open, Service Rifle, Match Rifle"
                  />
                  {formData.divisions.length > 1 && (
                    <IconButton
                      icon={<DeleteIcon />}
                      onClick={() => removeDivision(index)}
                      colorScheme="red"
                      variant="ghost"
                    />
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Categories */}
          <Box>
            <HStack justify="space-between" mb={4}>
              <Heading size="md">Categories</Heading>
              <Button size="sm" leftIcon={<AddIcon />} onClick={addCategory}>
                Add Category
              </Button>
            </HStack>

            <VStack spacing={2} align="stretch">
              {formData.categories.map((category, index) => (
                <HStack key={index}>
                  <Input
                    value={category}
                    onChange={(e) => handleCategoryChange(index, e.target.value)}
                    placeholder="e.g., Adult, Junior, Senior, Master"
                  />
                  {formData.categories.length > 1 && (
                    <IconButton
                      icon={<DeleteIcon />}
                      onClick={() => removeCategory(index)}
                      colorScheme="red"
                      variant="ghost"
                    />
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Stages */}
          <Box>
            <HStack justify="space-between" mb={4}>
              <Heading size="md">Stages</Heading>
              <Button size="sm" leftIcon={<AddIcon />} onClick={addStage}>
                Add Stage
              </Button>
            </HStack>

            <Accordion allowMultiple defaultIndex={[0]}>
              {stages.map((stage, index) => (
                <AccordionItem key={index}>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold">
                          {stage.stage_name} - {stage.scoring_primitive}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Stage Name</FormLabel>
                        <Input
                          value={stage.stage_name}
                          onChange={(e) =>
                            handleStageChange(index, 'stage_name', e.target.value)
                          }
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Number of Targets</FormLabel>
                        <NumberInput
                          value={stage.target_count || 1}
                          onChange={(value) =>
                            handleStageChange(index, 'target_count', parseInt(value) || 1)
                          }
                          min={1}
                        >
                          <NumberInputField />
                        </NumberInput>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          How many targets are in this stage
                        </Text>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Target Aggregation Method</FormLabel>
                        <Select
                          value={stage.target_aggregation_method}
                          onChange={(e) =>
                            handleStageChange(index, 'target_aggregation_method', e.target.value)
                          }
                        >
                          <option value="SUM">Sum (add all target scores)</option>
                          <option value="AVERAGE">Average (mean of target scores)</option>
                          <option value="BEST_OF">Best Of (highest target score)</option>
                          <option value="TOTAL_TIME">Total Time (sum of target times)</option>
                          <option value="LOWEST_TIME">Lowest Time (fastest target)</option>
                        </Select>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          How to combine scores from multiple targets within this stage
                        </Text>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Scoring Type</FormLabel>
                        <Select
                          value={stage.scoring_primitive}
                          onChange={(e) =>
                            handleStageChange(index, 'scoring_primitive', e.target.value)
                          }
                        >
                          <option value="SCORING_RING">Scoring Ring (bullseye scoring)</option>
                          <option value="HIT_MISS">Hit/Miss (binary scoring)</option>
                          <option value="TIME_BASED">Time Based (fastest time)</option>
                          <option value="COUNT_BASED">Count Based (number of hits)</option>
                          <option value="PLACEMENT">Placement (rank order)</option>
                          <option value="ACCUMULATION">Accumulation (total score)</option>
                          <option value="HIT_FACTOR">Hit Factor (points/time)</option>
                          <option value="TIME_PLUS">Time Plus (time + penalties)</option>
                        </Select>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          How individual targets are scored
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Weight (for weighted scoring)</FormLabel>
                        <NumberInput
                          value={stage.weight}
                          onChange={(value) =>
                            handleStageChange(index, 'weight', parseFloat(value) || 1.0)
                          }
                          min={0}
                          step={0.1}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>

                      {formData.stage_aggregation_method === 'MATCH_POINTS' && (
                        <FormControl>
                          <FormLabel>Stage Points (for match points scoring)</FormLabel>
                          <NumberInput
                            value={stage.stage_points || 100}
                            onChange={(value) =>
                              handleStageChange(index, 'stage_points', parseInt(value) || 100)
                            }
                            min={1}
                            step={10}
                          >
                            <NumberInputField />
                          </NumberInput>
                          <Text fontSize="xs" color="gray.600" mt={1}>
                            Points awarded to stage winner (typically 100)
                          </Text>
                        </FormControl>
                      )}

                      {/* Stage Parameters */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>
                          Stage Parameters
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {/* SCORING_RING: max_score, shots_per_target, x_count, inner_tens, clean_targets */}
                          {stage.scoring_primitive === 'SCORING_RING' && (
                            <>
                              <FormControl>
                                <FormLabel>Max Score</FormLabel>
                                <NumberInput
                                  value={stage.parameters.max_score || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.max_score',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Max Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Min Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.min_shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.min_shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={stage.parameters.x_count || false}
                                  onChange={(e) =>
                                    handleStageChange(
                                      index,
                                      'parameters.x_count',
                                      e.target.checked
                                    )
                                  }
                                >
                                  Track X-Count (for tiebreakers)
                                </Checkbox>
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={stage.parameters.inner_tens || false}
                                  onChange={(e) =>
                                    handleStageChange(
                                      index,
                                      'parameters.inner_tens',
                                      e.target.checked
                                    )
                                  }
                                >
                                  Track Inner Tens (for tiebreakers)
                                </Checkbox>
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={stage.parameters.clean_targets || false}
                                  onChange={(e) =>
                                    handleStageChange(
                                      index,
                                      'parameters.clean_targets',
                                      e.target.checked
                                    )
                                  }
                                >
                                  Track Clean Targets (for tiebreakers)
                                </Checkbox>
                              </FormControl>
                            </>
                          )}

                          {/* HIT_MISS: shots_per_target only */}
                          {stage.scoring_primitive === 'HIT_MISS' && (
                            <FormControl>
                              <FormLabel>Max Shots Per Target</FormLabel>
                              <NumberInput
                                value={stage.parameters.shots_per_target || ''}
                                onChange={(value) =>
                                  handleStageChange(
                                    index,
                                    'parameters.shots_per_target',
                                    parseInt(value) || 0
                                  )
                                }
                              >
                                <NumberInputField />
                              </NumberInput>
                              <Text fontSize="xs" color="gray.600" mt={1}>
                                Shots per target in this stage
                              </Text>
                            </FormControl>
                          )}

                          {/* TIME_BASED: time_limit, par_time */}
                          {stage.scoring_primitive === 'TIME_BASED' && (
                            <>
                              <FormControl>
                                <FormLabel>Time Limit (seconds, optional)</FormLabel>
                                <NumberInput
                                  value={stage.parameters.time_limit || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.time_limit',
                                      parseFloat(value) || null
                                    )
                                  }
                                  step={0.1}
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Par Time (seconds, optional)</FormLabel>
                                <NumberInput
                                  value={stage.parameters.par_time || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.par_time',
                                      parseFloat(value) || null
                                    )
                                  }
                                  step={0.1}
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <Text fontSize="sm" color="gray.600" p={2} bg="gray.50" borderRadius="md">
                                Time-based scoring: Fastest time wins. No additional parameters needed.
                              </Text>
                            </>
                          )}

                          {/* COUNT_BASED: shots_per_target */}
                          {stage.scoring_primitive === 'COUNT_BASED' && (
                            <FormControl>
                              <FormLabel>Max Shots Per Target</FormLabel>
                              <NumberInput
                                value={stage.parameters.shots_per_target || ''}
                                onChange={(value) =>
                                  handleStageChange(
                                    index,
                                    'parameters.shots_per_target',
                                    parseInt(value) || 0
                                  )
                                }
                              >
                                <NumberInputField />
                              </NumberInput>
                              <Text fontSize="xs" color="gray.600" mt={1}>
                                Shots per target available
                              </Text>
                            </FormControl>
                          )}

                          {/* HIT_FACTOR: max_score, shots_per_target, time_limit */}
                          {stage.scoring_primitive === 'HIT_FACTOR' && (
                            <>
                              <FormControl>
                                <FormLabel>Max Score</FormLabel>
                                <NumberInput
                                  value={stage.parameters.max_score || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.max_score',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Max Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Min Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.min_shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.min_shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Time Limit (seconds, optional)</FormLabel>
                                <NumberInput
                                  value={stage.parameters.time_limit || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.time_limit',
                                      parseFloat(value) || null
                                    )
                                  }
                                  step={0.1}
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <Text fontSize="xs" color="gray.600" p={2} bg="blue.50" borderRadius="md">
                                Hit Factor = Points ÷ Time. Higher is better.
                              </Text>
                            </>
                          )}

                          {/* TIME_PLUS: max_score, shots_per_target, time_limit */}
                          {stage.scoring_primitive === 'TIME_PLUS' && (
                            <>
                              <FormControl>
                                <FormLabel>Max Score</FormLabel>
                                <NumberInput
                                  value={stage.parameters.max_score || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.max_score',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Max Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Min Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.min_shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.min_shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Time Limit (seconds, optional)</FormLabel>
                                <NumberInput
                                  value={stage.parameters.time_limit || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.time_limit',
                                      parseFloat(value) || null
                                    )
                                  }
                                  step={0.1}
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <Text fontSize="xs" color="gray.600" p={2} bg="blue.50" borderRadius="md">
                                Time Plus = Time + Penalties. Lower is better.
                              </Text>
                            </>
                          )}

                          {/* PLACEMENT, ACCUMULATION: max_score, shots_per_target (generic) */}
                          {(stage.scoring_primitive === 'PLACEMENT' || stage.scoring_primitive === 'ACCUMULATION') && (
                            <>
                              <FormControl>
                                <FormLabel>Max Score</FormLabel>
                                <NumberInput
                                  value={stage.parameters.max_score || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.max_score',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Max Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>

                              <FormControl>
                                <FormLabel>Min Shots Per Target</FormLabel>
                                <NumberInput
                                  value={stage.parameters.min_shots_per_target || ''}
                                  onChange={(value) =>
                                    handleStageChange(
                                      index,
                                      'parameters.min_shots_per_target',
                                      parseInt(value) || 0
                                    )
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </FormControl>
                            </>
                          )}
                        </VStack>
                      </Box>

                      {stages.length > 1 && (
                        <Button
                          colorScheme="red"
                          variant="outline"
                          leftIcon={<DeleteIcon />}
                          onClick={() => removeStage(index)}
                        >
                          Remove Stage
                        </Button>
                      )}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>

          <Divider />

          {/* Tie-Breaking Rules */}
          <Box>
            <Heading size="md" mb={4}>
              Tie-Breaking Rules
            </Heading>

            <FormControl mb={4}>
              <Checkbox
                isChecked={tiebreakerEnabled}
                onChange={(e) => setTiebreakerEnabled(e.target.checked)}
              >
                Enable tie-breaking for this format
              </Checkbox>
            </FormControl>

            {tiebreakerEnabled && (
              <>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Tie-breaker rules are applied sequentially when competitors have the same total score.
                  Add one or more steps to define how ties should be broken.
                </Text>

                <VStack spacing={4} align="stretch">
                  {tiebreakerSteps.map((step, index) => (
                    <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                      <HStack justify="space-between" mb={3}>
                        <Text fontWeight="bold">Step {step.order}</Text>
                        <IconButton
                          size="sm"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => removeTiebreakerStep(index)}
                          aria-label="Remove step"
                        />
                      </HStack>

                      <VStack spacing={3} align="stretch">
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Type</FormLabel>
                          <Select
                            size="sm"
                            value={step.type}
                            onChange={(e) => updateTiebreakerStep(index, 'type', e.target.value)}
                          >
                            <option value="AGGREGATE_SCORE_FIELD">Aggregate Score Field (e.g., Total X Count)</option>
                            <option value="STAGE_SCORE">Specific Stage Score</option>
                            <option value="SHOOTOFF">Shootoff</option>
                            <option value="CUSTOM_FIELD">Custom Field</option>
                            <option value="SHARED_PLACEMENT">Shared Placement (Tie)</option>
                          </Select>
                        </FormControl>

                        {step.type === 'AGGREGATE_SCORE_FIELD' && (
                          <>
                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Field Name</FormLabel>
                              <Select
                                size="sm"
                                value={step.field || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'field', e.target.value)}
                              >
                                <option value="x_count">X Count</option>
                                <option value="inner_tens">Inner Tens</option>
                                <option value="clean_targets">Clean Targets</option>
                                <option value="total_time">Total Time</option>
                              </Select>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Direction</FormLabel>
                              <HStack>
                                <Button
                                  size="sm"
                                  variant={step.direction === 'DESC' ? 'solid' : 'outline'}
                                  colorScheme={step.direction === 'DESC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'DESC')}
                                >
                                  Higher Wins
                                </Button>
                                <Button
                                  size="sm"
                                  variant={step.direction === 'ASC' ? 'solid' : 'outline'}
                                  colorScheme={step.direction === 'ASC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'ASC')}
                                >
                                  Lower Wins
                                </Button>
                              </HStack>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Display Label</FormLabel>
                              <Input
                                size="sm"
                                value={step.label || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'label', e.target.value)}
                                placeholder="e.g., Total X Count"
                              />
                            </FormControl>
                          </>
                        )}

                        {step.type === 'STAGE_SCORE' && (
                          <>
                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Stage</FormLabel>
                              <Select
                                size="sm"
                                value={step.stage_id || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'stage_id', e.target.value)}
                                placeholder="Select stage"
                              >
                                {stages.map((stage, idx) => (
                                  <option key={idx} value={stage.stage_name}>
                                    {stage.stage_name}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Direction</FormLabel>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme={step.direction === 'DESC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'DESC')}
                                >
                                  Higher Wins
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme={step.direction === 'ASC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'ASC')}
                                >
                                  Lower Wins
                                </Button>
                              </HStack>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Display Label</FormLabel>
                              <Input
                                size="sm"
                                value={step.label || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'label', e.target.value)}
                                placeholder="e.g., 600 Yard Prone"
                              />
                            </FormControl>
                          </>
                        )}

                        {step.type === 'SHOOTOFF' && (
                          <>
                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Shootoff Type</FormLabel>
                              <Select
                                size="sm"
                                value={step.shootoff_type || 'FIXED_TARGETS'}
                                onChange={(e) => updateTiebreakerStep(index, 'shootoff_type', e.target.value)}
                              >
                                <option value="FIXED_TARGETS">Fixed Number of Targets</option>
                                <option value="SUDDEN_DEATH">Sudden Death</option>
                                <option value="CUSTOM">Custom</option>
                              </Select>
                            </FormControl>

                            {step.shootoff_type === 'FIXED_TARGETS' && (
                              <FormControl isRequired>
                                <FormLabel fontSize="sm">Target Count</FormLabel>
                                <Input
                                  size="sm"
                                  type="number"
                                  value={step.target_count || ''}
                                  onChange={(e) => updateTiebreakerStep(index, 'target_count', parseInt(e.target.value) || 0)}
                                  placeholder="e.g., 5"
                                />
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                  Number of targets in the shootoff (score is hits out of this number)
                                </Text>
                              </FormControl>
                            )}

                            {step.shootoff_type === 'SUDDEN_DEATH' && (
                              <Text fontSize="sm" color="gray.600" p={2} bg="gray.50" borderRadius="md">
                                Sudden death: Competitors shoot until one hits and the other misses. No target count needed.
                              </Text>
                            )}

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Display Label</FormLabel>
                              <Input
                                size="sm"
                                value={step.label || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'label', e.target.value)}
                                placeholder="e.g., 5-Target Shootoff"
                              />
                            </FormControl>
                          </>
                        )}

                        {step.type === 'CUSTOM_FIELD' && (
                          <>
                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Field Name</FormLabel>
                              <Input
                                size="sm"
                                value={step.field || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'field', e.target.value)}
                                placeholder="e.g., reverse_animal_count"
                              />
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Direction</FormLabel>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme={step.direction === 'DESC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'DESC')}
                                >
                                  Higher Wins
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme={step.direction === 'ASC' ? 'blue' : 'gray'}
                                  onClick={() => updateTiebreakerStep(index, 'direction', 'ASC')}
                                >
                                  Lower Wins
                                </Button>
                              </HStack>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Display Label</FormLabel>
                              <Input
                                size="sm"
                                value={step.label || ''}
                                onChange={(e) => updateTiebreakerStep(index, 'label', e.target.value)}
                                placeholder="e.g., Reverse Animal Count"
                              />
                            </FormControl>
                          </>
                        )}

                        {step.type === 'SHARED_PLACEMENT' && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Display Label</FormLabel>
                            <Input
                              size="sm"
                              value={step.label || ''}
                              onChange={(e) => updateTiebreakerStep(index, 'label', e.target.value)}
                              placeholder="e.g., Tied"
                            />
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              Both competitors will share the same rank
                            </Text>
                          </FormControl>
                        )}
                      </VStack>
                    </Box>
                  ))}

                  <Button
                    size="sm"
                    leftIcon={<AddIcon />}
                    onClick={addTiebreakerStep}
                    variant="outline"
                  >
                    Add Tie-Breaker Step
                  </Button>
                </VStack>
              </>
            )}
          </Box>

          <Divider />

          {/* Submit Buttons */}
          <HStack pt={4}>
            <Button type="submit" colorScheme="blue" isLoading={loading}>
              Create Format
            </Button>
            <Button onClick={() => navigate('/matches/manage')}>Cancel</Button>
          </HStack>
        </VStack>
      </form>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};
